import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../../css/shop.scss";
import Header from "../common/Header";
import Footer from "../common/Footer";
import Pagination from "../common/Pagination";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { get_product_list } from "../../apis/GetProductList";
import { get_category_list } from "../../apis/GetCategoryList";
import { BASE_URL } from "../../constans/ApiEndpointConstants";
import { sendMessage } from "../../apis/SendMessageAPI";
import LocalStorageUtil from "../../utils/localStorageUtil";
import { 
  isGbpLanguage,
} from "../../constans/CurrencyConstants";
import {
  pickDosageLine,
  pickMinPriceNumber,
  pickProductTitle,
} from "../../utils/productCardHelpers";

const Shop = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const tr = (key, fallback) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };
  const locale = (i18n.language || "nl").toLowerCase() === "nl" ? "nl" : "uk";
  const emptyTitle = tr(
    "shop.emptyTitle",
    locale === "nl" ? "Geen producten gevonden" : "No products found"
  );
  const emptyDesc = tr(
    "shop.emptyDesc",
    locale === "nl"
      ? "Probeer een andere categorie of zoekterm."
      : "Try a different category or keyword."
  );
  const clearBtnText = tr(
    "shop.clearFilters",
    locale === "nl" ? "Filters wissen" : "Clear filters"
  );
  const keywordParam = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search);
      const kw = sp.get("keyword");
      return kw ? decodeURIComponent(kw) : "";
    } catch {
      return "";
    }
  }, [location.search]);
  const [activePage, setActivePage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const PER_PAGE = 12;
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [pagePinged, setPagePinged] = useState(false);
  const [categories, setCategories] = useState([
    { id: "", key: "all", label: t("shop.categories.all") },
  ]);
  const [productCounts, setProductCounts] = useState({});

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateToDetail = (id, title) => {
    const slug =
      (title || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-+|-+$/g, "") || "product";
    const path = id ? `${slug}_${id}` : slug;
    navigate(`/shopDetails/${encodeURIComponent(path)}`);
    toTop();
  };

  useEffect(() => {
    toTop();
  }, []);

  // 进入 Shop 页面上报一次
  useEffect(() => {
    if (pagePinged) return;
    const gen = () => {
      const ch =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let r = "";
      for (let i = 0; i < 6; i++)
        r += ch[Math.floor(Math.random() * ch.length)];
      return r;
    };
    let userid = localStorage.getItem("userid");
    if (!userid) {
      userid = gen();
      localStorage.setItem("userid", userid);
    }
    const ad = LocalStorageUtil.getAd?.() ?? "PurityBase";
    sendMessage(`${BASE_URL} [Shop Page]:ad=${ad}:userid=${userid}`);
    setPagePinged(true);
  }, [pagePinged]);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          get_category_list(1, 100),
          get_product_list(1, 1000, null),
        ]);

        const prodData = prodRes?.data;
        const prodList =
          prodData?.data?.list ||
          prodData?.data?.items ||
          prodData?.list ||
          prodData?.rows ||
          [];

        const counts = {};
        prodList.forEach((p) => {
          const cid = String(p?.categoryid ?? p?.categoryId ?? "");
          if (cid) {
            counts[cid] = (counts[cid] || 0) + 1;
          }
        });

        if (alive) {
          setProductCounts(counts);
        }

        const data = catRes?.data;
        const list = data?.data?.list || data?.list || data?.data?.items || [];
        const lang =
          (i18n.language || "nl").toLowerCase() === "nl" ? "nl" : "uk";
        const mapped = Array.isArray(list)
          ? list.map((it) => {
              const id = String(it?.id ?? it?.categoryid ?? "");
              const label =
                it?.[`categoryname_${lang}`] ??
                it?.[`name_${lang}`] ??
                it?.categoryname ??
                it?.name ??
                "";
              const count =
                counts[id] ?? it?.product_count ?? it?.count ?? null;
              return { id, key: id, label, count };
            })
          : [];
        if (!alive) return;
        setCategories([
          { id: "", key: "all", label: t("shop.categories.all") },
          ...mapped.filter((c) => c.id),
        ]);
      } catch (e) {
        // 保留“全部”分类，即使分类接口失败也不影响页面其它功能
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [i18n.language, t]);

  const formatEuro = useMemo(() => {
    return (n) => {
      const isUk = isGbpLanguage(i18n.language);
      const locale = isUk ? "en-GB" : "nl-NL";
      const currency = "EUR";
      const amount = n;
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
        .format(amount)
        .replace(/\u00A0/g, " ");
    };
  }, [i18n.language]);

  const withBase = (src) => {
    if (!src) return null;
    if (/^https?:\/\//i.test(src) || src.startsWith("//")) return src;
    return `${BASE_URL}${src.startsWith("/") ? src : `/${src}`}`;
  };

  const langSuffix =
    (i18n.language || "nl").toLowerCase() === "nl" ? "nl" : "uk";

  const pickImage = useCallback((it) => {
    let pl = null;
    if (Array.isArray(it?.productlink) && it.productlink.length > 0) {
      const first = it.productlink[0];
      if (typeof first === "string") {
        pl = first;
      } else if (first && typeof first === "object") {
        pl = first.url || first.path || first.fileurl || first.link || null;
      }
    }
    const candidates = [
      pl,
      it?.cover,
      it?.image,
      it?.img,
      it?.thumbnail,
      it?.thumb,
      Array.isArray(it?.images) ? it.images[0] : null,
    ].filter(Boolean);
    const chosen = candidates[0];
    return withBase(chosen) || "/main/main1.avif";
  }, []);

  const formatPriceRange = useCallback(
    (it) => {
      const fmt = (x) => {
        if (x == null || x === "") return null;
        const num = Number(x);
        if (!isNaN(num)) return formatEuro(num);
        return `${x}`;
      };
      const min =
        it?.price_min ?? it?.min_price ?? it?.minPrice ?? it?.lowest_price;
      const max =
        it?.price_max ?? it?.max_price ?? it?.maxPrice ?? it?.highest_price;
      const single =
        it?.price ?? it?.sale_price ?? it?.priceNew ?? it?.price_new;
      if (min != null || max != null) {
        if (min != null && max != null && Number(min) !== Number(max)) {
          return `${fmt(min)} - ${fmt(max)}`;
        }
        return fmt(min ?? max) || "";
      }
      return fmt(single) || "";
    },
    [formatEuro]
  );

  const mapShopItem = useCallback(
    (it) => {
      const minN = pickMinPriceNumber(it, langSuffix);
      const priceLine =
        minN != null
          ? t("main.priceFrom", { price: formatEuro(minN) })
          : formatPriceRange(it) || "";
      return {
        id: it?.id,
        title: pickProductTitle(it, langSuffix),
        subtitle: pickDosageLine(it, langSuffix),
        priceLine,
        image: pickImage(it),
        status: it?.status,
        _raw: it,
      };
    },
    [formatEuro, formatPriceRange, langSuffix, pickImage, t]
  );

  useEffect(() => {
    let alive = true;
    const run = async () => {
      setLoading(true);
      try {
        const res = await get_product_list(
          activePage,
          PER_PAGE,
          keywordParam || null
        );
        const data = res?.data;
        const list =
          data?.data?.list ||
          data?.data?.items ||
          data?.list ||
          data?.rows ||
          [];
        const total =
          data?.data?.total || data?.total || data?.data?.count || 0;
        if (!alive) return;
        setProducts(list.map(mapShopItem));
        setTotalPages(
          total && Number(total) > 0
            ? Math.max(1, Math.ceil(Number(total) / PER_PAGE))
            : 1
        );
      } catch (e) {
        if (!alive) return;
        setProducts([]);
        setTotalPages(1);
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [activePage, keywordParam, mapShopItem]);

  return (
    <div>
      <Header />
      <main className="shop-page">
        <div className="shop__container">
          <header className="shop__hero">
            <nav
              className="shop__breadcrumb"
              aria-label={t("shop.breadcrumbAria")}
            >
              <Link to="/" className="shop__breadcrumb-link">
                {t("shop.breadcrumb")}
              </Link>
            </nav>
            <h1 className="shop__title">{t("shop.heroTitle")}</h1>
            <p className="shop__subtitle">{t("shop.heroSubtitle")}</p>
          </header>
          <div className="shop__layout">
            <aside
              className={`filters ${filtersOpen ? "open" : "collapsed"}`}
              aria-label={t("shop.filtersTitle")}
            >
              <div
                className="filters__header"
                onClick={() => setFiltersOpen((v) => !v)}
              >
                <span className="filters__title">
                  {t("shop.filtersTitle", "Filter")}
                </span>
                <button
                  className="filters__collapse"
                  aria-label={t("shop.toggleFiltersAria")}
                >
                  {filtersOpen ? (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="18 15 12 9 6 15"></polyline>
                    </svg>
                  ) : (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  )}
                </button>
              </div>
              <div className="filters__subtitle">
                {t("shop.categoriesTitle", "CATEGORIES")}
              </div>
              {filtersOpen && (
                <div className="filters__list">
                  {categories.map((c, i) => {
                    const isAll = c.id === "";
                    const isActive =
                      (isAll && !keywordParam) ||
                      (!isAll && c.id === keywordParam);
                    return (
                      <button
                        key={`cat-${i}`}
                        className={`filter-item ${isActive ? "active" : ""}`}
                        onClick={() => {
                          const kw = isAll ? "" : c.id;
                          setActivePage(1);
                          navigate(
                            kw
                              ? `/shop?keyword=${encodeURIComponent(kw)}`
                              : `/shop`
                          );
                          toTop();
                        }}
                      >
                        <span className="filter-item__check">
                          {isActive && (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                        </span>
                        <span className="filter-item__label">
                          {c.label || t(`shop.categories.${c.key || "all"}`)}
                        </span>
                        {c.count != null && (
                          <span className="filter-item__count">{c.count}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </aside>
            <section className="shop__grid" aria-label={t("shop.productsAria")}>
              {loading && (
                <div className="shop__loading" role="status" aria-live="polite">
                  <span className="spinner" aria-hidden="true"></span>
                  <span className="sr-only">
                    {t("common.loading") || "Loading..."}
                  </span>
                </div>
              )}
              {!loading &&
                products.map((p, i) => (
                  <div
                    className="product-card"
                    key={p.id != null ? `prod-${p.id}` : `prod-${i}`}
                    onClick={() => {
                      navigateToDetail(p.id, p.title);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigateToDetail(p.id, p.title);
                      }
                    }}
                  >
                    <div className="product-card__image">
                      <img alt={p.title} src={p.image} />
                      {p.status === 1 && (
                        <div className="product-card__outOfStockBadge">
                          {t("main.buttons.outOfStock") || "Out of Stock"}
                        </div>
                      )}
                    </div>
                    <div className="product-card__body">
                      <div className="product-card__title">{p.title}</div>

                      <div
                        className="product-card__divider"
                        aria-hidden="true"
                      />
                      <div className="product-card__footer">
                        <div className="product-card__fromPrice">
                          {p.priceLine}
                        </div>
                        {p.status === 1 ? (
                          <div className="product-card__outOfStockText">
                            {t("main.buttons.outOfStock", "OUT OF STOCK")}
                          </div>
                        ) : (
                          <buuton
                            className="product-card__btn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigateToDetail(p.id, p.title);
                            }}
                          >
                            {t("main.buttons.view", "View")}
                          </buuton>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              {!loading && products.length === 0 && (
                <div className="shop__empty" role="region" aria-live="polite">
                  <div className="empty__icon" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      width="64"
                      height="64"
                      fill="currentColor"
                    >
                      <path d="M21 11H6.414l3.293-3.293-1.414-1.414L2.586 12l5.707 5.707 1.414-1.414L6.414 13H21v-2z"></path>
                    </svg>
                  </div>
                  <div className="empty__title">{emptyTitle}</div>
                  <div className="empty__desc">{emptyDesc}</div>
                  <div className="empty__actions">
                    <button
                      type="button"
                      className="empty__btn"
                      onClick={() => {
                        setActivePage(1);
                        navigate("/shop");
                        toTop();
                      }}
                    >
                      {clearBtnText}
                    </button>
                  </div>
                </div>
              )}
              {products.length > 0 && totalPages > 1 && (
                <Pagination
                  totalPages={totalPages}
                  currentPage={activePage}
                  onChange={setActivePage}
                />
              )}
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Shop;
